"use strict";

/**
 * Resolução de `familia.tom` contra as paletas de gráfico.
 *
 * Os nomes de família aqui são slots de uma paleta temável, não descrições de cor: no
 * `theme2`, `cyan` é verde e `violet` é laranja. O vocabulário gravado em
 * `chart.series[].color` é o legado — `cyan`, `magenta`, `violet`, `basic` —, mas o
 * design system do pro-v2 renomeou as mesmas famílias, e por um tempo o v2 gravou os nomes
 * novos. Daí o mapa de alias.
 *
 * Antes disto, token desconhecido devolvia `undefined` e a série saía com a cor default do
 * Highcharts, ou a string crua passava adiante e a série saía preta. Nos dois casos, sem log.
 */

/** Nomes da paleta do pro-v2 → slot legado equivalente. Mesmos hex no tema `default`. */
const FAMILY_ALIASES = {
    blue: "cyan",
    terracota: "magenta",
    terracotta: "magenta",
    green: "violet",
    petroleum: "basic"
};

/** Grafias erradas que existem em produção. `cyan.disable` nasceu do `SeriesENUM` do back-api. */
const SHADE_ALIASES = {
    disable: "disabled",
    desabled: "disabled",
    daker: "darker",
    lightest: "lighter",
    darkest: "darker"
};

/** Ordem de busca quando o tom pedido não existe naquela família. */
const SHADE_FALLBACK_ORDER = ["medium", "dark", "light", "darker", "lighter"];

export const FALLBACK_COLOR_PATH = "goals.medium";

/**
 * Parte das visualizações grava cor CSS direto em vez de token. `rgba(46, 85, 115, .9)` tem
 * ponto e `#1a72a8` não tem separador nenhum: sem este atalho os dois virariam lixo no
 * `splitPath` e cairiam no fallback.
 */
const CSS_LITERAL_PREFIXES = ["#", "rgb", "hsl", "var(", "transparent"];

function isCssLiteral(value) {
    return CSS_LITERAL_PREFIXES.some((prefix) => value.startsWith(prefix));
}

/** Aceita `familia.tom` e `familia-tom`; alguns pontos do back gravam com hífen. */
function splitPath(pathName) {
    if (typeof pathName !== "string") return null;
    const trimmed = pathName.trim();
    if (!trimmed) return null;
    const separator = trimmed.indexOf(".") > -1 ? "." : "-";
    const index = trimmed.indexOf(separator);
    if (index < 1) return null;
    return {
        family: trimmed.slice(0, index),
        shade: trimmed.slice(index + 1)
    };
}

function toColor(candidate, theme) {
    if (!candidate) return null;
    return candidate[theme] || candidate.light || null;
}

/** O tom pedido, ou a grafia certa dele. Nada de vizinhos. */
function exactShade(shades, shade, theme) {
    if (!shades) return null;
    return toColor(shades[shade] || shades[SHADE_ALIASES[shade]], theme);
}

/** O tom mais próximo que a família tiver. */
function siblingShade(shades, theme) {
    if (!shades) return null;
    const name = SHADE_FALLBACK_ORDER.find((it) => Boolean(shades[it]));
    return name ? toColor(shades[name], theme) : null;
}

/**
 * A família pedida vem antes da aliasada, e o tom exato vem antes do vizinho: um tema que
 * tenha a família mas não o tom deve cair num vizinho dela, não numa família diferente.
 */
function pickFromPalette(palette, family, shade, theme) {
    if (!palette) return null;
    const own = palette[family];
    const aliased = palette[FAMILY_ALIASES[family]];
    return (
        exactShade(own, shade, theme) ||
        exactShade(aliased, shade, theme) ||
        siblingShade(own, theme) ||
        siblingShade(aliased, theme)
    );
}

/**
 * @param {Object} palette paleta ativa
 * @param {Object} defaultPalette paleta `default`, usada quando a ativa não tem a família
 * @param {String} pathName token gravado, ex. `cyan.medium`
 * @param {String} theme `light` ou `dark`
 * @return {String} sempre uma cor válida
 */
export function resolveColorPath(palette, defaultPalette, pathName, theme) {
    if (typeof pathName === "string" && isCssLiteral(pathName.trim())) {
        return pathName.trim();
    }
    const parts = splitPath(pathName);
    if (parts) {
        const color =
            pickFromPalette(palette, parts.family, parts.shade, theme) ||
            pickFromPalette(defaultPalette, parts.family, parts.shade, theme);
        if (color) return color;
    }
    const fallback = splitPath(FALLBACK_COLOR_PATH);
    return (
        pickFromPalette(palette, fallback.family, fallback.shade, theme) ||
        pickFromPalette(defaultPalette, fallback.family, fallback.shade, theme)
    );
}

/** Só o par `familia.tom` normalizado, sem consultar paleta. Útil para logs e comparação. */
export function normalizeColorPath(pathName) {
    const parts = splitPath(pathName);
    if (!parts) return "";
    const family = FAMILY_ALIASES[parts.family] || parts.family;
    const shade = SHADE_ALIASES[parts.shade] || parts.shade;
    return `${family}.${shade}`;
}

export { FAMILY_ALIASES, SHADE_ALIASES, SHADE_FALLBACK_ORDER };
